import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  NgZone,
  OnDestroy,
  ChangeDetectionStrategy,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { CallFacadeService } from '@app/services/call';
import { NotificationsService } from '../../services/notifications/notifications.service';

/**
 * CompactCallButton - A simple call trigger button
 *
 * This component is a lightweight button for initiating calls.
 * It does NOT manage call state or display call progress.
 * For full call UI (status, duration, controls), see GlobalCallPanelComponent.
 *
 * Uses OnPush change detection with signals for optimal performance.
 */
@Component({
  selector: 'app-compact-call-button',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './compact-call-button.component.html',
  styleUrls: ['./compact-call-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompactCallButtonComponent implements OnDestroy {
  // Inputs as signals for reactivity
  private _phoneNumber = signal<string>('');
  private _leadId = signal<number | undefined>(undefined);
  private _leadName = signal<string | undefined>(undefined);
  private _size = signal<'small' | 'medium'>('medium');
  private _showText = signal<boolean>(true);

  @Input()
  set phoneNumber(value: string) {
    this._phoneNumber.set(value);
  }

  @Input()
  set leadId(value: number | undefined) {
    this._leadId.set(value);
  }

  @Input()
  set leadName(value: string | undefined) {
    this._leadName.set(value);
  }

  @Input()
  set size(value: 'small' | 'medium') {
    this._size.set(value);
  }

  @Input()
  set showText(value: boolean) {
    this._showText.set(value);
  }

  @Output() callInitiated = new EventEmitter<{ leadId?: number; phoneNumber: string }>();

  private ngZone = inject(NgZone);
  private callFacade = inject(CallFacadeService);
  private notifications = inject(NotificationsService);

  // Local state as signals
  isProcessing = signal<boolean>(false);

  // Computed values - only recalculate when dependencies change
  hasActiveCall = computed(() => {
    const state = this.callFacade.currentState();
    return ['dialing', 'ringing', 'connecting', 'connected', 'held', 'post-call'].includes(state);
  });

  canInitiateCall = computed(() => {
    return !!this._phoneNumber() &&
           !this.isProcessing() &&
           !this.hasActiveCall();
  });

  // Expose signals for template
  showTextValue = computed(() => this._showText());

  buttonClass = computed(() => {
    const classes = ['compact-call-button'];

    if (this._size() === 'small') {
      classes.push('compact-call-button--small');
    }

    if (this.hasActiveCall()) {
      classes.push('compact-call-button--active');
    }

    return classes.join(' ');
  });

  tooltipText = computed(() => {
    const phone = this._phoneNumber();
    if (!phone) {
      return 'No phone number available';
    }

    if (this.isProcessing()) {
      return 'Initiating call...';
    }

    if (this.hasActiveCall()) {
      return 'Another call is in progress';
    }

    return `Call ${phone}`;
  });

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Handle call button click
   */
  onCallButtonClick(): void {
    if (this.canInitiateCall()) {
      this.initiateCall();
    }
  }

  /**
   * Initiate an outbound call
   */
  private async initiateCall(): Promise<void> {
    const phone = this._phoneNumber();
    if (!this.canInitiateCall() || !phone) {
      return;
    }

    try {
      this.isProcessing.set(true);

      const success = await this.callFacade.initiateCall(phone, this._leadId(), this._leadName());

      if (success) {
        this.ngZone.run(() => {
          this.callInitiated.emit({
            leadId: this._leadId(),
            phoneNumber: phone
          });
        });
      } else {
        // Show error to user
        this.ngZone.run(() => {
          const errorMessage = this.callFacade.lastError() || 'Failed to initiate call';
          this.notifications.showClientError(errorMessage);
        });
      }
    } catch (error) {
      console.error('CompactCallButton: Failed to initiate call:', error);
      this.ngZone.run(() => {
        this.notifications.showClientError('Failed to initiate call');
      });
    } finally {
      this.ngZone.run(() => {
        this.isProcessing.set(false);
      });
    }
  }
}
